import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import type { GetUserAssembliesFilters } from "@tudigo-monorepo/core-tudigo-api";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { ASSEMBLIES } from "@tudigo-monorepo/core-tudigo-utils";
import {
  createFilterHelper,
  type FilterConfiguration,
} from "@tudigo-monorepo/web-tudigo-collection";

const ParticipationsAssembliesFiltersContext = createContext<{
  configuration: Array<FilterConfiguration<GetUserAssembliesFilters>>;
  filters: GetUserAssembliesFilters;
  setFilters: Dispatch<SetStateAction<GetUserAssembliesFilters>>;
} | null>(null);

export function useParticipationsAssembliesFilters() {
  const context = useContext(ParticipationsAssembliesFiltersContext);
  if (context == null) {
    throw new Error(
      "useParticipationsAssembliesFilters must be used within a ParticipationsAssembliesFiltersProvider",
    );
  }

  return context;
}

type ParticipationsAssembliesFiltersProviderProps = {
  children: ReactNode;
  defaultFilters?: GetUserAssembliesFilters;
};

export function ParticipationsAssembliesFiltersProvider({
  children,
  defaultFilters = {},
}: ParticipationsAssembliesFiltersProviderProps) {
  const [filters, setFilters] =
    useState<GetUserAssembliesFilters>(defaultFilters);

  const { t } = useTranslation();
  const filterHelper = createFilterHelper<GetUserAssembliesFilters>();

  const configuration: FilterConfiguration<GetUserAssembliesFilters>[] = [
    filterHelper.organizationSelect(
      "refId",
      t("participations.web_app.assembly_list_page.filters.holding.label"),
      {
        type: "company",
      },
    ),
    filterHelper.select(
      "status",
      t("participations.web_app.assembly_list_page.filters.status.label"),
      Object.values(ASSEMBLIES.STATUS).map((status) => ({
        value: status,
        name: t(
          `participations.web_app.assembly_list_page.assembly.status.${status}`,
        ),
      })),
    ),
  ];

  const contextValue = useMemo(
    () => ({ configuration, filters, setFilters }),
    [configuration, filters],
  );

  return (
    <ParticipationsAssembliesFiltersContext.Provider value={contextValue}>
      {children}
    </ParticipationsAssembliesFiltersContext.Provider>
  );
}
